import { useEffect, useRef } from 'react';
import { firestoreTracker } from './firestoreTracker';

export const useFirestoreDebugger = () => {
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    //Console commands
    (window as any).fsDebug = {
      //Print report
      report: (minutes = 60) => {
        return firestoreTracker.printReport(minutes);
      },
      
      //Find leaks
      findLeaks: () => {
        firestoreTracker.findLeaks();
      },
      
      //JSON report
      export: () => {
        const json = firestoreTracker.exportToJSON();
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        console.log('Report generated. Copy this URL to download:', url);
        return url;
      },
      
      //Clear tracked data
      clear: () => {
        firestoreTracker.clear();
      },
      
      //Print report
      analyze: () => {
        const report = firestoreTracker.getReport(60);
        
        console.group('FIRESTORE ANALYSIS REPORT');
        
        //Report
        console.group('CRITICAL ISSUES:');
        
        //Listeners in inactive forests
        const listenOps = report.topOperations.filter((op: any) => 
          op.key.includes('listen') || op.key.includes('Listen')
        );
        
        if (listenOps.length > 0) {
          console.error('Active listeners detected when no forests should be active:');
          listenOps.forEach((op: any) => {
            console.error(`  - ${op.key}: ${op.count} times`);
            console.error(`    Stack traces:`, op.stackTraces);
          });
        }
        
        //Rapid polling check
        const rapidReads = report.topOperations.filter((op: any) => 
          op.count > 50 && !op.isRealtime
        );
        
        if (rapidReads.length > 0) {
          console.error('Rapid repeated reads detected (possible polling):');
          rapidReads.forEach((op: any) => {
            console.error(`  - ${op.key}: ${op.count} times`);
            console.error(`    Functions:`, op.stackTraces);
          });
        }
        
        console.groupEnd();
        
        //Show reads
        console.group('TOP READ SOURCES:');
        const readOps = report.topOperations
          .filter((op: any) => op.key.includes('read'))
          .slice(0, 10);
        
        readOps.forEach((op: any, i: number) => {
          console.log(`${i + 1}. ${op.path || op.key}`);
          console.log(`   Count: ${op.count}`);
          console.log(`   Sources:`, op.stackTraces);
        });
        console.groupEnd();
        
        console.groupEnd();
      },
      
      //Monitor: 5 mins
      monitor: () => {
        console.log('Starting 5-minute monitoring session...');
        firestoreTracker.clear();
        
        intervalRef.current = setInterval(() => {
          const report = firestoreTracker.getReport(1);
          const totalOps = report.totalOperations;
          if (totalOps > 0) {
            console.log(`[Monitor] ${new Date().toLocaleTimeString()}: ${totalOps} ops in last minute`);
          }
        }, 60000);
        
        setTimeout(() => {
          if (intervalRef.current) clearInterval(intervalRef.current);
          console.log('Monitoring complete. Generating report...');
          (window as any).fsDebug.analyze();
        }, 300000);
      },
      
      //Stop monitoring
      stopMonitor: () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          console.log('Monitoring stopped');
        }
      },
      
      //Check/Find specific patterns
      checkPattern: (pattern: string) => {
        const report = firestoreTracker.getReport(60);
        const matches = report.topOperations.filter((op: any) => 
          op.key.includes(pattern) || op.path?.includes(pattern)
        );
        
        if (matches.length > 0) {
          console.group(`Pattern "${pattern}" found:`);
          matches.forEach((match: any) => {
            console.log(`Operation: ${match.key}`);
            console.log(`Count: ${match.count}`);
            console.log(`Sources:`, match.stackTraces);
          });
          console.groupEnd();
        } else {
          console.log(`No operations matching "${pattern}" found`);
        }
      },
      
      //Find orphaned listeners
      findOrphans: () => {
        console.group('Checking for orphaned listeners...');
        
        //Check specific listener patterns
        const patterns = [
          'forests/*/trees',
          'forests/*/userPresence',
          'forests/*/exerciseCompletions',
          'forests/*/waterActions',
          'users',
          'dailyExercises'
        ];
        
        const report = firestoreTracker.getReport(60);
        patterns.forEach(pattern => {
          const matches = report.topOperations.filter((op: any) => 
            op.key.includes('listen') && op.key.includes(pattern)
          );
          
          if (matches.length > 0) {
            console.warn(`⚠️ Listener on "${pattern}": ${matches[0].count} operations`);
          }
        });
        
        console.groupEnd();
      },
      
      //Emil 24 hour report
      email: () => {
        const report = firestoreTracker.getReport(1440); //24 hours
        const summary = `
Firestore Debug Report
Generated: ${new Date().toISOString()}

SUMMARY:
- Total Operations: ${report.totalOperations}
- Reads: ${report.operationsByType.reads}
- Writes: ${report.operationsByType.writes}
- Listeners: ${report.operationsByType.listens}

TOP ISSUES:
${report.suspiciousPatterns.map((p: any) => 
  `- ${p.type}: ${p.path} (${p.count} times)`
).join('\n')}

TOP OPERATIONS:
${report.topOperations.slice(0, 10).map((op: any) => 
  `- ${op.key}: ${op.count} times`
).join('\n')}

Full JSON report attached as console output.
        `;
        
        const mailto = `mailto:your-email@example.com?subject=Firestore Debug Report&body=${encodeURIComponent(summary)}`;
        console.log('Email draft created. Copy this link:', mailto);
        console.log('Full Report:', JSON.stringify(report, null, 2));
        return mailto;
      }
    };
    
    //Instructions
    console.log(`
Firestore Debugger Ready! Commands:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
fsDebug.report(60)    - Show report for last 60 minutes
fsDebug.analyze()     - Run full analysis
fsDebug.monitor()     - Monitor for 5 minutes
fsDebug.findLeaks()   - Find potential memory leaks
fsDebug.findOrphans() - Find orphaned listeners
fsDebug.export()      - Export JSON report
fsDebug.email()       - Create email report
fsDebug.clear()       - Clear tracked data
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
};